/**
 * DEMO · Idea 2 — ¿Qué me falta?
 *
 * Real (del modelo): qué piezas quedan cubiertas por la HP, y el % presupuestado por piezas, área y
 * volumen, sumando las medidas reales. Lo que no se construye se deja fuera por CLASE del estándar
 * (espacios, vanos, elementos virtuales, anotaciones; el terreno no es un elemento constructivo).
 * De ejemplo: qué conceptos trae la HP a medio armar.
 */
import { useMemo, useRef, useState } from 'react';
import { Switch } from '@fluentui/react-components';
import { DemoVisor3D, type Pintura, type VisorDemoHandle } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel, estiloTabla } from '../../components/demos/DemoMarco.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import {
  HP_EJEMPLO,
  coloresDemo,
  conceptoDe,
  crearContexto,
  nombreClase,
  num0,
  num2,
} from '../../components/demos/logicaDemo.js';
import datos from '../../components/demos/datos/que-me-falta.json';
import { LoQueFalta } from '../../components/demos/LoQueFalta.js';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'que-me-falta')!;
const EN_HP = HP_EJEMPLO.conceptos.filter((c) => datos.conceptosPresupuestados.includes(c.id));

export function DemoQueMeFalta() {
  const visor = useRef<VisorDemoHandle>(null);
  const [modelo, setModelo] = useState<ModeloDemo | null>(null);
  const [verFalta, setVerFalta] = useState(true);
  const [foco, setFoco] = useState<string | null>(null);
  const colores = useMemo(() => coloresDemo(), []);

  const r = useMemo(() => {
    if (!modelo) return null;
    const ctx = crearContexto(modelo.elementos);
    const cubiertos = new Set<string>();
    for (const e of modelo.elementos) if (conceptoDe(e, ctx, EN_HP)) cubiertos.add(e.clave);
    const suma = (f: (e: ElementoDemo) => number, sel: (e: ElementoDemo) => boolean) =>
      modelo.elementos.filter(sel).reduce((s, e) => s + f(e), 0);
    const todos = () => true;
    const si = (e: ElementoDemo) => cubiertos.has(e.clave);
    const medidas = [
      { id: 'piezas', etiqueta: 'por piezas', total: modelo.elementos.length, hecho: modelo.elementos.filter(si).length, fmt: num0, u: 'piezas' },
      { id: 'area', etiqueta: 'por área', total: suma((e) => e.area ?? 0, todos), hecho: suma((e) => e.area ?? 0, si), fmt: num2, u: 'm²' },
      { id: 'volumen', etiqueta: 'por volumen', total: suma((e) => e.volumen ?? 0, todos), hecho: suma((e) => e.volumen ?? 0, si), fmt: num2, u: 'm³' },
    ];
    // Lo que falta, agrupado por clase del modelo (muro, puerta, viga…).
    const faltan = new Map<string, ElementoDemo[]>();
    for (const e of modelo.elementos) if (!si(e)) (faltan.get(e.clase) ?? faltan.set(e.clase, []).get(e.clase)!).push(e);
    return { cubiertos, medidas, faltan: [...faltan.entries()].sort((a, b) => b[1].length - a[1].length) };
  }, [modelo]);

  const focoSet = useMemo(() => new Set(r?.faltan.find(([t]) => t === foco)?.[1].map((e) => e.clave) ?? []), [r, foco]);
  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        if (!el) return { visible: false };
        if (!verFalta || !r) return {};
        if (focoSet.has(clave)) return { color: colores.acento, opacidad: 1 };
        if (r.cubiertos.has(clave)) return { color: colores.neutro, opacidad: 0.12 };
        return { color: colores.bad, opacidad: 1 };
      },
    [verFalta, r, focoSet, colores],
  );

  const principal = r?.medidas[0];
  const pctDe = (m: { total: number; hecho: number }) => (m.total ? (m.hecho / m.total) * 100 : 0);

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <p style={{ margin: '0 0 12px', fontSize: 'var(--fs-400)', maxWidth: 820 }}>
        En <b style={{ color: 'var(--bad)' }}>rojo</b> está lo que tu modelo tiene y todavía no está en tu hoja de presupuesto. Lo gris ya
        está en tu hoja.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(360px, 1fr)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch checked={verFalta} onChange={(_, d) => setVerFalta(d.checked)} label="Ver lo que falta" />
            {verFalta && (
              <span style={{ display: 'inline-flex', gap: 14, fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--bad)' }} /> Sin presupuestar
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--text-tertiary)', opacity: 0.4 }} /> Ya presupuestado
                </span>
              </span>
            )}
          </div>
          <DemoVisor3D
            ref={visor}
            fuentes={[{ clave: 'a', url: '/demos/fzk-haus-a.ifc' }]}
            onListo={(m) => setModelo(m.a ?? null)}
            pintar={pintar}
            alto={540}
          />
        </div>
        <Panel titulo="Antes de mandar tu propuesta">
          {r && principal ? (
            <>
              <div style={{ background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                <div style={{ color: 'var(--text-secondary)' }}>Has presupuestado el</div>
                <div style={{ fontSize: 'var(--fs-700, 32px)', fontWeight: 700, lineHeight: 1.1 }}>
                  {Math.round(pctDe(principal))} % <span style={{ fontSize: 'var(--fs-400)', fontWeight: 500 }}>del modelo</span>
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-200)' }}>contado por piezas</div>
              </div>
              {r.medidas.map((m) => (
                <div key={m.id}>
                  <div style={{ display: 'flex', fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
                    <span style={{ flex: 1 }}>{m.etiqueta}</span>
                    <span style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                      {Math.round(pctDe(m))} % · {m.fmt(m.hecho)} de {m.fmt(m.total)} {m.u}
                    </span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bad-weak)', borderRadius: 4, marginTop: 3 }}>
                    <div style={{ width: `${pctDe(m)}%`, height: '100%', background: 'var(--ok)', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
              <h3 style={{ margin: '6px 0 0', fontSize: 'var(--fs-300)' }}>Lo que falta en tu hoja</h3>
              <table style={estiloTabla.tabla}>
                <thead>
                  <tr>
                    <th style={estiloTabla.th}>Qué es</th>
                    <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Piezas</th>
                  </tr>
                </thead>
                <tbody>
                  {r.faltan.map(([tipo, els]) => (
                    <tr
                      key={tipo}
                      onClick={() => {
                        setFoco(foco === tipo ? null : tipo);
                        setVerFalta(true);
                        visor.current?.encuadrar(els.map((e) => e.clave));
                      }}
                      style={{ cursor: 'pointer', background: foco === tipo ? 'var(--accent-weak)' : undefined }}
                    >
                      <td style={estiloTabla.td}>
                        {nombreClase(tipo)}
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {[...new Set(els.map((e) => e.tipo))].slice(0, 3).join(' · ')}
                          {new Set(els.map((e) => e.tipo)).size > 3 ? ' · …' : ''}
                        </div>
                      </td>
                      <td style={estiloTabla.num}>{els.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <NotaEjemplo>
                No cuentan, porque no se construyen: {modelo!.noSeConstruye.map((x) => `${nombreClase(x.clase).toLowerCase()}: ${x.cantidad}`).join('; ')}
                {modelo!.noSeConstruye.length ? '' : 'nada'}, ni el terreno. Se reconocen por su clase en el modelo, no por su nombre.
              </NotaEjemplo>
              <NotaEjemplo>{datos.aviso} Conceptos en la HP: {EN_HP.map((c) => c.clave).join(', ')}.</NotaEjemplo>
            </>
          ) : (
            <div style={{ color: 'var(--text-secondary)' }}>Midiendo el modelo…</div>
          )}
        </Panel>
      </div>
      <LoQueFalta modelo={modelo} conceptosEnHoja={EN_HP.map((c) => c.id)} />
    </DemoMarco>
  );
}
